import { Routes as ReactRouterRoutes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";
import Loading from "../pages/loading";
import SearchPage from "../pages/search/search-page";
import CallBack from "../components/auth/callback";
import { Logout } from "../components/auth/logout";
import { LogoutCallback } from "../components/auth/logout-callback";
import { SilentRenew } from "../components/auth/silent-renew";
import SecureRoute from "./secure-route";
import PrivateRoute from "./private-route";
import { PERMISSIONS } from "../constants";
import ROUTES from "./constants";
import NotAuthorizedPage from "../pages/errors/not-authorized-page";
import TasksPage from "../pages/tasks/tasks-page";
import ControlPanelPage from "../pages/admin/control-panel/control-panel-page";
import ClientConfigPage from "../pages/admin/client-config/client-config-page";
import WatchListPage from "../pages/admin/watch-list/watch-list-page";
import WatchListAddPage from "../pages/admin/watch-list/watch-list-add-page";
import ClientUploadPage from "../pages/admin/client-upload/client-upload-page";
import { LD_FLAGS } from "../components/launchdarkly/flags";
import JunkValuesPage from "../pages/admin/junk-values/junk-values-page";
import IgnoreListPage from "../pages/admin/ignore-list/ignore-list-page";
import ScoringRulesPage from "../pages/admin/scoring-rules/scoring-rules-page";
import ClientScoringRulesPage from "../pages/admin/scoring-rules/client-scoring-rules";
import CleaningPage from "../pages/admin/cleaning/cleaning-page";
import DataCleaningPage from "../pages/data-cleaning/data-cleaning-page";
import TemplatesPage from "../pages/admin/templates/templates-page";
import FilterOptionsPage from "../pages/admin/filter-options/filter-options-page";
import MdaSpreadsheetMappingsPage from "../pages/admin/mda-spreadsheet-mappings/mda-spreadsheet-mappings-page";
import FileMonitorPage from "../pages/file-monitor/file-monitor-page";
import FileExplorerPage from "../pages/admin/file-explorer/file-explorer-page";
import ClientIngestionPage from "../pages/admin/client-ingestion/client-ingestion-page";
import StatusPage from "../pages/status/status-page";
import UnusedSqlObjectsPage from "../pages/monitoring/unused-sql-objects-page";
import MdaPage from "../pages/mda/mda-page";
import AuditPage from "../pages/admin/audit/audit-page";
import CachePage from "../pages/admin/cache/cache-page";

const HomePage = lazy(() => import("../pages/home-page"));
const NotFoundPage = lazy(() => import("../pages/errors/not-found-page"));

const PublicRoutes = () => (
  <>
    <Route path={ROUTES.CALLBACK} element={<CallBack />} />
    <Route path={ROUTES.LOGOUT} element={<Logout />} />
    <Route path={ROUTES.LOGOUT_CALLBACK} element={<LogoutCallback />} />
    <Route path={ROUTES.SILENT_RENEW} element={<SilentRenew />} />
    <Route path={ROUTES.NOT_AUTHORIZED} element={<NotAuthorizedPage />} />
    <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
    <Route path="*" element={<NotFoundPage />} />
  </>
);

const Routes: React.FC = () => {
  return (
    <Suspense fallback={<Loading />}>
      <ReactRouterRoutes>
        {PublicRoutes()}
        <Route element={<SecureRoute />}>
          <Route
            path={ROUTES.HOME}
            element={
              <PrivateRoute requiredPermission={PERMISSIONS.VIEW_ADMIN}>
                <HomePage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.TASKS}
            element={
              <PrivateRoute requiredPermission={PERMISSIONS.VIEW_ADMIN}>
                <TasksPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.STATUS}
            element={
              <PrivateRoute requiredPermission={PERMISSIONS.VIEW_ADMIN}>
                <StatusPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.MDA}
            element={
              <PrivateRoute
                requiredPermission={PERMISSIONS.VIEW_WATCHLIST}
                ldFlag={LD_FLAGS.SHOW_MDA}
              >
                <MdaPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.MONITORING.UNUSED_SQL_OBJECTS}
            element={
              <PrivateRoute requiredPermission={PERMISSIONS.VIEW_ADMIN}>
                <UnusedSqlObjectsPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.FILE_MONITOR}
            element={
              <PrivateRoute requiredPermission={PERMISSIONS.VIEW_ADMIN}>
                <FileMonitorPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.FILE_MONITOR_CLIENTS}
            element={
              <PrivateRoute requiredPermission={PERMISSIONS.VIEW_ADMIN}>
                <FileMonitorPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.FILE_MONITOR_CLIENT}
            element={
              <PrivateRoute requiredPermission={PERMISSIONS.VIEW_ADMIN}>
                <FileMonitorPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.FILE_EXPLORER}
            element={
              <PrivateRoute requiredPermission={PERMISSIONS.VIEW_ADMIN}>
                <FileExplorerPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.FILE_EXPLORER_FOLDERS}
            element={
              <PrivateRoute requiredPermission={PERMISSIONS.VIEW_ADMIN}>
                <FileExplorerPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.FILE_EXPLORER_FOLDER}
            element={
              <PrivateRoute requiredPermission={PERMISSIONS.VIEW_ADMIN}>
                <FileExplorerPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.DATA_CLEANING}
            element={
              <PrivateRoute
                requiredPermission={PERMISSIONS.VIEW_WATCHLIST}
                ldFlag={LD_FLAGS.SHOW_DATA_CLEANSING}
              >
                <DataCleaningPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.CLEANING}
            element={
              <PrivateRoute requiredPermission={PERMISSIONS.VIEW_ADMIN}>
                <CleaningPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.SEARCH}
            element={
              <PrivateRoute requiredPermission={PERMISSIONS.VIEW_SEARCH}>
                <SearchPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.ADMIN}
            element={
              <PrivateRoute requiredPermission={PERMISSIONS.VIEW_ADMIN}>
                <ControlPanelPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.CONTROL_PANEL}
            element={
              <PrivateRoute requiredPermission={PERMISSIONS.VIEW_ADMIN}>
                <ControlPanelPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.AUDIT}
            element={
              <PrivateRoute requiredPermission={PERMISSIONS.VIEW_ADMIN}>
                <AuditPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.CACHE}
            element={
              <PrivateRoute requiredPermission={PERMISSIONS.VIEW_ADMIN}>
                <CachePage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.CLIENTS}
            element={
              <PrivateRoute
                requiredPermission={PERMISSIONS.VIEW_ADMIN}
                ldFlag={LD_FLAGS.SHOW_CLIENT_CONFIG}
              >
                <ClientConfigPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.WATCH_LIST}
            element={
              <PrivateRoute
                requiredPermission={PERMISSIONS.VIEW_WATCHLIST}
                ldFlag={LD_FLAGS.SHOW_WATCH_LIST}
              >
                <WatchListPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.WATCH_LIST_ADD}
            element={
              <PrivateRoute
                requiredPermission={PERMISSIONS.VIEW_WATCHLIST}
                ldFlag={LD_FLAGS.SHOW_WATCH_LIST}
              >
                <WatchListAddPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.FILE_UPLOAD}
            element={
              <PrivateRoute
                requiredPermission={PERMISSIONS.VIEW_ADMIN}
                ldFlag={LD_FLAGS.SHOW_CLIENT_UPLOAD}
              >
                <ClientUploadPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.JUNK_VALUES}
            element={
              <PrivateRoute
                requiredPermission={PERMISSIONS.VIEW_ADMIN}
                ldFlag={LD_FLAGS.SHOW_JUNK_VALUES}
              >
                <JunkValuesPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.IGNORE_LIST}
            element={
              <PrivateRoute
                requiredPermission={PERMISSIONS.VIEW_ADMIN}
                ldFlag={LD_FLAGS.SHOW_IGNORE_LIST}
              >
                <IgnoreListPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.SCORING_RULES}
            element={
              <PrivateRoute
                requiredPermission={PERMISSIONS.VIEW_ADMIN}
                ldFlag={LD_FLAGS.SHOW_SCORING_RULES}
              >
                <ScoringRulesPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.CLIENT_SCORING_RULES}
            element={
              <PrivateRoute
                requiredPermission={PERMISSIONS.VIEW_ADMIN}
                ldFlag={LD_FLAGS.SHOW_SCORING_RULES}
              >
                <ClientScoringRulesPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.SCORING}
            element={
              <PrivateRoute
                requiredPermission={PERMISSIONS.VIEW_ADMIN}
                ldFlag={LD_FLAGS.SHOW_SCORING_RULES}
              >
                <ScoringRulesPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.TEMPLATES}
            element={
              <PrivateRoute
                requiredPermission={PERMISSIONS.VIEW_ADMIN}
                ldFlag={LD_FLAGS.SHOW_TEMPLATES}
              >
                <TemplatesPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.FILTER_OPTIONS}
            element={
              <PrivateRoute
                requiredPermission={PERMISSIONS.VIEW_ADMIN}
                ldFlag={LD_FLAGS.SHOW_FILTER_OPTIONS}
              >
                <FilterOptionsPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.MDA_SPREADSHEET_MAPPINGS}
            element={
              <PrivateRoute
                requiredPermission={PERMISSIONS.VIEW_ADMIN}
                ldFlag={LD_FLAGS.SHOW_MDA_SPREADSHEET_MAPPINGS}
              >
                <MdaSpreadsheetMappingsPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN.CLIENT_INGESTION}
            element={
              <PrivateRoute requiredPermission={PERMISSIONS.VIEW_ADMIN}>
                <ClientIngestionPage />
              </PrivateRoute>
            }
          />
        </Route>
      </ReactRouterRoutes>
    </Suspense>
  );
};

export default Routes;
